<script>
import { FormButton } from "@/components/form";
import LoadingView from "@/components/LoadingView.vue";
import { AttachmentsPane, InfoPane } from "@/components/media";
import { LogPane } from "@/components/shared";
import TabPanes from "@/components/TabPanes.vue";
import Media from "@/models/media";
import { GlobalPolicy } from "@/policies/global";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { handle404 } from '@/helpers/globals';
import { mapState } from "pinia";
import InfoText from "@/components/media/InfoText.vue";

let tabs = [
    {
        name: 'info',
        prefixIcon: 'fak',
        icon: 'fa-shield',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'attachments',
        prefixIcon: 'fak',
        icon: 'fa-link',
        title: 'Attached Locations',
        acl: 0
    },
    {
        name: 'logs',
        prefixIcon: 'fak',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    }
];
export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.media?.alt ?? 'Untitled'}`
        }
    },
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            media: new Media({id: this.$route.params.id}),
            collections: [],
            communities: [],
            floorplans: [],
            keywords: [],
            isLoading: true,
            tabs: [],
            prev: false
        }
    },
    methods: {
        async loadData() {
            try {
                const [mediaRes, collectionsRes, communitiesRes, floorplansRes, keywordsRes] = await Promise.all([
                    axios.get(`media/${this.media.id}`),
                    axios.get('collections'),
                    axios.get('divisions/communities'),
                    axios.get('floorplans'),
                    axios.get('keywords')
                ]);

                this.media = new Media(mediaRes.data);
                this.collections = collectionsRes.data.sort((a,b) => a.meta?.info?.name > b.meta?.info?.name ? 1 : -1);
                this.communities = communitiesRes.data
                    .map(community => {
                        community.collections = community.collections
                            .filter(collection => collection.status != 'inactive')
                            .sort((a,b) => a.meta.info.name > b.meta.info.name ? 1 : -1);
                        return community;
                    })
                    .filter(community => community.status != 'inactive' && community.collections.length > 0)
                    .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
                this.floorplans = floorplansRes.data;
                this.keywords = keywordsRes.data;
                this.isLoading = false;
            } catch(err) {
                if(err.response?.status === 404) {
                    handle404(this, err.response.data.message);
                }
                console.error(err);
            }
        },
        show() {
            axios.get(`media/${this.media.id}`)
                .then(res => {
                    this.media = new Media(res.data);
                    this.isLoading = false;
                })
                .catch(err => {
                    if(err.response.status === 404)
                        handle404(this, err.response.data.message);
                });
        },
        save() {
            this.isLoading = true;
            axios.patch(`media/${this.media.id}`, this.media)
                .then(res => {
                    this.isLoading = false;
                })
                .catch(e => {
                    throw e;
                });
        },
        prepare(value) {
            this.media = new Media(value);
            this.save();
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user'])
    },
    created() {
        this.loadData();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to,from,next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        LoadingView,
        FormButton,
        TabPanes,
        AttachmentsPane,
        InfoPane,
        LogPane,
        InfoText
    }
}
</script>

<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton 
                    color="secondary-bg-white"
                    icon="fa-chevron-left" 
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col sm:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-8 flex-1 justify-between order-2 lg:order-1">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="font-headline antialiased leading-9 text-3xl  font-medium bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text ">
                                {{ media.alt }}
                            </h1>
                        </div>
                    </div>
                    <div class="flex flex-col w-full lg:flex-row gap-2">
                        <InfoText label="Created Date" icon="fak fa-history" :value="media.date" class="w-full" />
                        <InfoText label="Type" icon="fak fa-blog" :value="media.type" />
                        <InfoText label="Dimensions" icon="fak fa-expand" :value="media.dimensions.width + ' X ' + media.dimensions.height" />
                    </div>
                    <div class="flex flex-col w-full lg:flex-row gap-2">
                        <InfoText label="File Name" icon="fak fa-sign" :value="media.filename" class="w-full" />
                        <InfoText label="Creator" icon="fas fa-user" :value="media.creator?.name" />
                        <InfoText label="File Size" icon="fak fa-ruler" :value="media.size" />
                    </div>

                </div>
                <div class="w-full lg:w-64 flex order-1 lg:order-2 aspect-square">
                    <img v-if="media.url" class="object-cover h-full w-full rounded-md"
                        :src="media.url" 
                        :alt="media.caption"
                        :title="media.alt">
                    <div v-else class="text-5xl bg-bhav-blue-300 rounded-md text-white p-8 flex items-center justify-center w-full h-full">
                        <span class="fak fa-floorplan"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="media-tab" :tabs="tabs" v-slot="{tab}" class="gap-8">
            <InfoPane :item="media" :communities="communities" :floorplans="floorplans" :keywords="keywords" @update="prepare" v-if="tab?.name === 'info'" />
            <AttachmentsPane :id="media.id" :collections="collections" v-if="tab?.name === 'attachments'" />
            <LogPane context="media" v-if="tab?.name === 'logs'" />
        </TabPanes>
    </div>
</template>